import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { PHASE_PRODUCTION_BUILD } from 'next/constants';
import { useTranslation } from 'next-i18next';

import { vacanciesSlugs } from '@boss/constants/corporate-jobs';
import { getEntriesByTag } from '@boss/contentful-client/corporate-jobs';
import { job } from '@boss/services';
import { Environment, ImageFields, SectionFields } from '@boss/types/corporate-jobs';

import SEO from '../../components/SEO';
import { PageTypes } from '../../constants/page';
import { JobsPage } from '../../containers';
import Layout from '../../layout/default';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import { mapNextToContentfulLocaleMarket } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';
import { showJobPages } from '../../utils/options';

const IS_BUILD = process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD;

export const getStaticProps = async ({ preview, locale }: GetStaticPropsContext) => {
  if (!locale || locale === 'default' || !showJobPages) {
    return {
      notFound: true,
    };
  }

  const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(locale);

  try {
    const [navigation, jobs, enrichedSections, contentfulBannerImage] = await Promise.all([
      getMappedNavigation(contentfulLocaleMarket, !!preview),
      job.getVacancies({ locale }),
      getEntriesByTag<SectionFields>('displayLinkOtherJobsSiteOverview', contentfulLocaleMarket),
      getEntriesByTag<ImageFields>('displayBannerImageJobsOverview', contentfulLocaleMarket),
    ]);

    const bannerImage = contentfulBannerImage?.[0]
      ? {
          id: contentfulBannerImage?.[0]?.id ?? '',
          imageUrl: contentfulBannerImage?.[0]?.image?.file?.url ?? undefined,
          imageAlt: contentfulBannerImage?.[0]?.altText ?? '',
        }
      : null;

    // ENRICHMENT OF JOBS ARRAY
    const detailedJobs = await Promise.all(
      jobs.map(vacancy => {
        return job.getVacancy(vacancy.vacatureId.toString(), locale ?? 'nl');
      }),
    ).then(data => {
      return data;
    });

    const filteredDetailedJobs = detailedJobs.filter(job => job?.publicatieTaal === locale);

    // LOCATIONS
    let locaties: string[][] = [];

    filteredDetailedJobs?.forEach(job => {
      if (job?.handigeWeetjes?.locatie) {
        locaties = [...locaties, job.handigeWeetjes?.locatie];
      }
    });

    const flattenedLocations: string[] = locaties.flat();

    const uniqueLocations: string[] = flattenedLocations.filter(
      (locatie, index) => flattenedLocations.indexOf(locatie) === index,
    );

    // TODO: Below is ugly and should be improved
    const hasNederland = uniqueLocations.includes('Nederland');

    const sortedLocations = hasNederland
      ? [...uniqueLocations.filter(location => location !== 'Nederland'), 'Nederland']
      : uniqueLocations;

    return {
      props: {
        pageType: PageTypes.JOB_OVERVIEW,
        preview: !!preview,
        locale,
        environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
        navigation,
        jobs: filteredDetailedJobs,
        locations: sortedLocations,
        enrichedSections,
        ...(await getServerSideTranslations(locale, ['common', 'jobs'])),
        bannerImage,
      },
    };
  } catch (error) {
    console.error(`[jobs/index] getStaticProps failed for locale "${locale}":`, error);

    if (IS_BUILD && error instanceof job.HroApiError) {
      throw error;
    }

    return { notFound: true };
  }
};

export const Index = ({
  preview,
  locale,
  environment,
  navigation,
  jobs,
  locations,
  enrichedSections,
  bannerImage,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation(['jobs']);
  const localizedSlugs = vacanciesSlugs;
  const pageTitle = t('pageTitle');

  return (
    <SEO
      description={pageTitle}
      imageSrc={jobs[0]?.banner?.url}
      localizedSlugs={localizedSlugs}
      navigation={navigation}
      noFollow
      noIndex
      title={pageTitle}
    >
      <Layout environment={environment} localizedSlugs={localizedSlugs} navigation={navigation} preview={preview}>
        <JobsPage
          bannerImage={bannerImage}
          enrichedSections={enrichedSections ?? undefined}
          jobs={jobs}
          locale={locale}
          locations={locations}
          pageTitle={pageTitle}
        />
      </Layout>
    </SEO>
  );
};

export default Index;
